// Ops device approval page (ADR-0029 section 3, `/ops/device`). Not linked from navigation.
// An operator signs in with Google, checks the env, the user code, the scopes and the device IP, then
// approves or denies. The page never receives an ops token.
import { useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { formatUserCode, type OpsDeviceInfo } from "../api/ops";
import type { AuthUser } from "../auth/auth";
import { authErrorMessage } from "../auth/auth";
import { useServices } from "../components/context";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { ErrorBanner, useAction } from "../components/ui";

type Done = { approved: boolean; env: string; scope: string };

/** A message for an ops API error. The server message stays visible through ErrorBanner for other codes. */
function opsError(err: unknown): unknown {
  if (err instanceof ApiError) {
    if (err.status === 404)
      return new Error("This code is not correct, has expired, or was used. Start again in the terminal.");
    if (err.status === 403)
      return new Error("This Google account is not an enabled platform operator for these scopes.");
  }
  return err;
}

export function OpsDevice({ user, initialCode }: { user: AuthUser | null; initialCode: string }) {
  const { auth, ops } = useServices();
  const [code, setCode] = useState(initialCode);
  const [info, setInfo] = useState<OpsDeviceInfo | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [reauth, setReauth] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const action = useAction();

  const [signingIn, setSigningIn] = useState(false);

  async function signIn() {
    setSignInError(null);
    setSigningIn(true);
    try {
      await auth.signInWithGoogle();
      setReauth(false);
    } catch (e) {
      setSignInError(authErrorMessage(e));
    } finally {
      setSigningIn(false);
    }
  }

  const header = (
    <>
      <h1>Approve an operator sign-in</h1>
      <p className="muted">A Prego admin tool asks for an ops token. Approve it only if you started it.</p>
    </>
  );

  if (!user || user.passwordAccount) {
    return (
      <main className="signin ops-device">
        {header}
        <div className="card">
          {user?.passwordAccount && (
            <p className="banner warn" role="status">
              Operators sign in with their Google account. Sign out, then sign in with Google.
            </p>
          )}
          {user?.passwordAccount ? (
            <button type="button" onClick={() => void auth.signOut()}>
              Sign out
            </button>
          ) : (
            <GoogleSignInButton disabled={signingIn} onClick={() => void signIn()} />
          )}
          {signInError && (
            <p className="banner error" role="alert">
              {signInError}
            </p>
          )}
        </div>
      </main>
    );
  }

  function check(e: FormEvent) {
    e.preventDefault();
    setInfo(null);
    void action.run(async () => {
      try {
        setInfo(await ops.lookup(code));
      } catch (err) {
        throw opsError(err);
      }
    });
  }

  function decide(approve: boolean) {
    void action.run(async () => {
      try {
        const r = await ops.decide(code, approve);
        setDone({ approved: r.ok, env: r.env, scope: r.scope });
      } catch (err) {
        if (err instanceof ApiError && err.code === "reauth_required") setReauth(true);
        throw opsError(err);
      }
    });
  }

  if (done) {
    return (
      <main className="signin ops-device">
        {header}
        <div className="card">
          <p className={`banner ${done.approved ? "ok" : "warn"}`} role="status">
            {done.approved
              ? `Approved for ${done.env}: ${done.scope}. Go back to the terminal. You can close this page.`
              : "Denied. The terminal gets no token. You can close this page."}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="signin ops-device">
      {header}
      <div className="card">
        <p className="small muted">Signed in as {user.email}</p>
        <form onSubmit={check}>
          <label>
            Code from the terminal
            <input
              className="mono"
              required
              autoComplete="off"
              placeholder="XXXX-XXXX"
              value={code}
              onChange={(e) => {
                setCode(formatUserCode(e.target.value));
                setInfo(null);
              }}
            />
          </label>
          <button type="submit" disabled={action.busy || code.length !== 9}>
            Check code
          </button>
        </form>
        <ErrorBanner error={action.error} />
        {reauth && <GoogleSignInButton disabled={signingIn} onClick={() => void signIn()} />}
        {info && (
          <div className="ops-details">
            <p className="ops-env" data-env={info.env} aria-label="Environment">
              {info.env.toUpperCase()}
            </p>
            <dl>
              <dt>Code</dt>
              <dd className="mono">{code}</dd>
              <dt>Scopes</dt>
              <dd className="mono">{info.scope}</dd>
              <dt>Device IP</dt>
              <dd className="mono">{info.device_ip ?? "unknown"}</dd>
              <dt>Expires in</dt>
              <dd>{Math.ceil(info.expires_in / 60)} min</dd>
            </dl>
            <p className="small">
              Make sure that the code is the same as in your terminal and that you started this request.
            </p>
            <div className="row">
              <button type="button" disabled={action.busy} onClick={() => decide(true)}>
                Approve
              </button>
              <button
                type="button"
                className="secondary"
                disabled={action.busy}
                onClick={() => decide(false)}
              >
                Deny
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
